import { useMemo } from 'react'

import { buildContentFilters } from 'contexts/content-filters'
import { useNicheScope } from 'contexts/niche-scope'
import { useTerritoryQueries } from 'hooks-queries/territory'
import {
  isIndustryCategoryTerritory,
  resolvePromotedNicheExcludedContextId,
  resolvePromotedScopeId,
} from 'modules/niche/common/promoted-niche.utils'

import { GET_PROMOTED_NICHE_TERRITORY_LOG } from './use-promoted-niche-context.consts'
import type { IUsePromotedNicheContext } from './use-promoted-niche-context.types'

/**
 * Single source of the Promoted-Niche derivations shared by the Niches tab and
 * the Demand Intelligence tab. From the territory's `promotionsV2` it resolves:
 * the source context to exclude, when to defer context auto-selection (so the
 * excluded context never leaks into `createNicheContextHash`), the promoted
 * scope id, and the scope filter / copy-query override derived from it.
 *
 * Promoted industries resolve their parent's contexts server-side, so the
 * `territoryId` is queried as-is.
 */
export const usePromotedNicheContext = (territoryId: string): IUsePromotedNicheContext => {
  const { data, loading: isTerritoryLoading } = useTerritoryQueries({
    queryName: 'getTerritoryTitle',
    errorLog: GET_PROMOTED_NICHE_TERRITORY_LOG,
    queryVariables: { territoryId },
  })

  const { scopeId } = useNicheScope()

  const territory = data?.getTerritory
  const isIndustryCategory = isIndustryCategoryTerritory(territoryId)

  const excludeContextId = resolvePromotedNicheExcludedContextId(territory)
  const promotedScopeId = resolvePromotedScopeId(scopeId, territoryId, territory)
  const scopedCategoryId = promotedScopeId ? promotedScopeId.split('.')[1] : undefined

  const parentTerritoryIdsOverride = useMemo<string[] | undefined>(
    () => (promotedScopeId ? [promotedScopeId] : undefined),
    [promotedScopeId]
  )
  const territoryContentFilters = useMemo(
    () => buildContentFilters(promotedScopeId ? [promotedScopeId] : null),
    [promotedScopeId]
  )

  // Defer auto-selection only on category pages, and only until the promotion
  // data settles — otherwise the manager can auto-select / hash the
  // to-be-excluded source context before the exclusion is known.
  const deferAutoSelect = isIndustryCategory && isTerritoryLoading

  return {
    excludeContextId,
    deferAutoSelect,
    promotedScopeId,
    scopedCategoryId,
    parentTerritoryIdsOverride,
    territoryContentFilters,
    territoryName: data?.getTerritory?.name ?? undefined,
    isLoading: isTerritoryLoading,
  }
}
